import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { ALL_PROVINCES, City, Province, Region, REGIONS } from '@toevla-webapplications/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Location } from './location.model';
import { LocationType } from './location-type.enum';

@Injectable({
	providedIn: 'root',
})
export class LocationsService {
	private readonly http = inject(HttpClient);
	private readonly translate = inject(TranslateService);

	initLocations(): Observable<Location[]> {
		return this.getCities().pipe(
			switchMap((cities) => {
				const cityLocations = cities.map((c) => this.convert(LocationType.CITY, c));

				return of([...cityLocations, ...this.getProvincesAsLocations(), ...this.getRegionsAsLocations()]);
			}),
			map((locations) => locations.sort((a, b) => a.name.localeCompare(b.name)))
		);
	}

	getCities(): Observable<City[]> {
		return this.http.get<string[]>('/rest/offers/cities').pipe(map((names) => names.map((name) => ({ city: name }) as City)));
	}

	private convert(type: LocationType, data: City | Province | Region): Location {
		const name = this.getName(type, data);

		return {
			name,
			type,
			data,
		};
	}

	private getName(type: LocationType, data: City | Province | Region): string {
		switch (type) {
			case LocationType.CITY:
				return (data as City).city;
			case LocationType.PROVINCE:
				return this.translate.instant(`PROVINCE.${data}`);
			case LocationType.REGION:
				return this.translate.instant(`REGION.${data}`);
		}
	}

	private getProvincesAsLocations(): Location[] {
		return ALL_PROVINCES.map((p) => this.convert(LocationType.PROVINCE, p));
	}

	private getRegionsAsLocations(): Location[] {
		return REGIONS.map((r) => this.convert(LocationType.REGION, r));
	}
}
